import { cn } from '../lib/cn'
import { Avatar } from './Avatar'

/**
 * Several people, as one quiet mark: small faces side by side, closing with
 * a count of the rest. Where a list of names would shout, the stack lets a
 * crew, an audience or a room be present without taking the row over — the
 * people behind it are one hover away, and the count says how many more.
 */
export function AvatarStack({
  people,
  max = 4,
  size = 'xs',
  className,
}: {
  people: ReadonlyArray<{ key: string; name?: string | null; email?: string | null; src?: string | null }>
  /** How many faces show before the count takes over. */
  max?: number
  size?: 'xs' | 'sm'
  className?: string
}) {
  if (people.length === 0) return null
  const shown = people.slice(0, max)
  const rest = people.length - shown.length
  const names = people
    .map((person) => person.name ?? person.email ?? '')
    .filter(Boolean)
    .join(', ')

  return (
    <span
      className={cn('flex items-center', className)}
      title={names || undefined}
      aria-label={`${people.length} ${people.length === 1 ? 'person' : 'people'}${names ? `: ${names}` : ''}`}
    >
      {/* Faces side by side with a hair of space, no outline: a ring in the
          surface's colour read as a thick dark circle in the dark theme. */}
      <span className="flex items-center gap-0.5">
        {shown.map((person) => (
          <Avatar key={person.key} name={person.name ?? null} email={person.email ?? ''} src={person.src} size={size} />
        ))}
        {rest > 0 ? (
          <span
            className={cn(
              'flex shrink-0 select-none items-center justify-center rounded-full bg-muted font-mono text-foreground tabular-nums',
              size === 'xs' ? 'size-5 text-[10px]' : 'size-7 text-caption',
            )}
          >
            +{rest}
          </span>
        ) : null}
      </span>
    </span>
  )
}
